import { useNavigate, useLocation } from 'react-router';
import { useSetAtom } from 'jotai';
import { categoryToggleAtom } from '@rocco/states/categoryToggleAtom';
import {
    CategoryToggle,
    type CategoryToggleValue,
} from './components/CategoryToggle';
import { CategoryPills } from './components/CategoryPills';
import { BackButton } from '@ui-rocco/pages/products-page/components/BackButton';

interface CategoryPillItem {
    id: number;
    title: string;
    link: string;
}

interface CategoryNavigationBarProps {
    activeToggle: CategoryToggleValue;
    onToggleChange: (toggle: CategoryToggleValue) => void;
    showToggle?: boolean;
    categories: CategoryPillItem[];
    /** Display name of the parent category (e.g., "Furniture"). Defaults to "All Product Categories" */
    parentCategoryName?: string;
    /** URL path to the parent category (e.g., "/products/furniture"). Defaults to "/products" */
    parentCategoryLink?: string;
}

/**
 * Navigation bar for category pages. On root, shows Interior/Building toggle
 * and category pills. On category pages, shows a back button to return
 * to the parent category, plus category pills.
 * @param activeToggle - The active toggle value.
 * @param onToggleChange - The function to call when the toggle value changes.
 * @param showToggle - Whether to show the toggle (true on root /products).
 * @param categories - The categories to display.
 * @param parentCategoryName - Display name of the parent category for the back button.
 * @param parentCategoryLink - URL path to the parent category for the back button.
 */
export const CategoryNavigationBar = ({
    activeToggle,
    onToggleChange,
    showToggle = true,
    categories,
    parentCategoryName = 'All Product Categories',
    parentCategoryLink = '/products',
}: CategoryNavigationBarProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const setCategoryToggle = useSetAtom(categoryToggleAtom);

    const isBuildingCategory =
        location.pathname === '/products/building-hardware' ||
        location.pathname.startsWith('/products/building-hardware/');

    const handleBackClick = () => {
        // Only set the category toggle when navigating back to the root /products page
        if (parentCategoryLink === '/products') {
            setCategoryToggle(isBuildingCategory ? 'building' : 'interior');
        }
        navigate(parentCategoryLink, {
            state: { restoreScroll: true, scroll: false },
        });
    };

    return (
        <div className="flex items-center gap-3 px-4 md:px-12">
            {/* Left controls group */}
            <div className="flex shrink-0 items-center gap-3">
                {/* Back button - only on category pages (not root) */}
                {!showToggle && (
                    <BackButton
                        parentCategoryName={parentCategoryName}
                        parentCategoryLink={parentCategoryLink}
                        onClick={handleBackClick}
                    />
                )}

                {/* Interior/Building Toggle - only on root products page */}
                {showToggle && (
                    <CategoryToggle
                        activeToggle={activeToggle}
                        onToggleChange={onToggleChange}
                    />
                )}
            </div>

            {/* Category Pills - scrollable carousel extending to right edge */}
            <CategoryPills categories={categories} />
        </div>
    );
};
